import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { NIKE_URL } from "@/data/constants/url";
import { useAuth } from "@/features/auth/shared/hooks/use-auth";
import { useLang } from "@/hooks/use-lang";
import { Lang } from "@/i18n-config";

import { Category } from "./get-category-data";

const videoStatusSchema = z.union([
  z.literal("unstarted"),
  z.literal("playing"),
  z.literal("completed"),
]);

const responseSchema = z.object({
  message: z.string().optional(),
  data: z.record(
    z.object({
      favorite: z.boolean(),
      progress: z.number().nullable(),
      history: z.record(
        z.object({
          status: videoStatusSchema,
          length: z.string(),
        })
      ),
    })
  ),
});

export type VideoStatus = z.infer<typeof videoStatusSchema>;
export type MediaBehaviorData = z.infer<typeof responseSchema>["data"];

export const getUserBehavior = async ({
  lang,
  token,
  category,
}: {
  lang: Lang;
  token: string;
  category: Category;
}): Promise<MediaBehaviorData> => {
  const endpoint = `/media-behavior/${category}`;
  const res = await fetch(`${NIKE_URL}${endpoint}`, {
    method: "GET",
    headers: { "Accept-Language": lang, "X-User-Profile": token },
    cache: "no-cache",
  });

  if (!res.ok) throw new Error(`API failed (${endpoint})`);
  return responseSchema.parse(await res.json()).data;
};

export const useUserBehavior = ({ category }: { category: Category }) => {
  const { lang } = useLang();
  const { backendToken } = useAuth();

  return useQuery(
    ["user-behavior", lang, category],
    () => getUserBehavior({ lang, token: backendToken ?? "", category }),
    {
      enabled: !!backendToken && !!category,
    }
  );
};
